// AgentOwnerMenu — "..." button + menu for agents the viewer owns:
// Edit, Copy link, Delete. Renders nothing for agents the viewer doesn't own.
//
// Used on AgentTile / AgentCard / AgentListRow so owner actions are
// reachable from every catalog surface, not just the chat header.

import React, { useCallback } from 'react';
import { Platform, Pressable, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useAgentStore, useModalStore, useToastStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import useCopyAgentLink from '@/hooks/useCopyAgentLink';
import Icon from '@/components/Icon';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import DropdownMenuItemDivider from '@/components/DropdownMenu/DropdownMenuItemDivider';

const isWeb = Platform.OS === 'web';

const stop = (e) => {
  e?.stopPropagation?.();
  if (typeof e?.preventDefault === 'function') e.preventDefault();
};

function AgentOwnerMenu({
  agent,
  size = 30,
  iconSize = 13,
  style,
  onMenuStateChange,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const openModal = useModalStore((s) => s.actions.openModal);
  const addToast = useToastStore((s) => s.actions.addToast);
  const deleteAgent = useAgentStore((s) => s.deleteAgent);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();
  const { copyAgentLink } = useCopyAgentLink();

  const handleOpenChange = useCallback((open) => {
    onMenuStateChange?.(open);
  }, [onMenuStateChange]);

  const handleEdit = useCallback((e) => {
    stop(e);
    openModal('CustomAgentModal', { agentId: agent.id });
  }, [openModal, agent?.id]);

  const handleCopyLink = useCallback(async (e) => {
    stop(e);
    await copyAgentLink(agent);
  }, [copyAgentLink, agent]);

  const handleDelete = useCallback((e) => {
    stop(e);
    showConfirmationModal({
      title: (t('pages.agents.deleteAgentTitle') || 'Delete %NAME%?').replace('%NAME%', agent?.name || 'this bot'),
      message: t('pages.agents.deleteAgentMessage')
        || 'This removes the bot from your list. Existing chats with it are kept.',
      confirmText: t('common.delete') || 'Delete',
      cancelText: t('common.cancel') || 'Cancel',
      isDangerous: true,
      onConfirm: async () => {
        try {
          await deleteAgent(agent.id);
          addToast(t('pages.agents.agentDeleted') || 'Bot deleted', 'success');
        } catch (err) {
          console.error('Failed to delete agent:', err);
          addToast(t('pages.agents.deleteAgentFailed') || 'Failed to delete bot', 'error');
        }
      },
    });
  }, [showConfirmationModal, deleteAgent, addToast, agent?.id, agent?.name, t]);

  if (!agent?.isOwner) return null;

  const editLabel = t('pages.agents.editAgent') || 'Edit bot';
  const copyLabel = t('pages.agents.copyLink') || 'Copy link';
  const deleteLabel = t('pages.agents.deleteAgent') || 'Delete bot';

  return (
    <View
      onClick={stop}
      onMouseDown={stop}
      onPointerDown={stop}
      style={{ position: 'relative' }}
    >
      <DropdownMenu.Root onOpenChange={handleOpenChange}>
        <DropdownMenu.Trigger>
          <Pressable
            onPress={stop}
            onPressIn={stop}
            hitSlop={4}
            accessibilityRole="button"
            accessibilityLabel={t('pages.agents.agentActions') || 'Bot actions'}
            style={({ hovered, pressed }) => [
              {
                width: size,
                height: size,
                borderRadius: 8,
                alignItems: 'center',
                justifyContent: 'center',
                ...(isWeb ? { cursor: 'pointer' } : {}),
              },
              (hovered || pressed) && { backgroundColor: colors.solid_color_secondary },
              style,
            ]}
          >
            <Icon name="ellipsis" size={iconSize} color={colors.text_color_secondary} />
          </Pressable>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          className={isWeb ? 'DropdownMenuContent' : undefined}
          align={isXS ? 'center' : 'end'}
          sideOffset={isXS ? 4 : 5}
          onCloseAutoFocus={(e) => {
            if (isWeb) e?.preventDefault?.();
          }}
        >
          {isWeb ? (
            <>
              <DropdownMenuItem key="edit" onSelect={handleEdit} icon="pencil" title={editLabel} />
              <DropdownMenuItem key="copy" onSelect={handleCopyLink} icon="link" title={copyLabel} />
              <DropdownMenuItemDivider key="divider" />
              <DropdownMenuItem key="delete" onSelect={handleDelete} icon="trash" title={deleteLabel} isDanger />
            </>
          ) : (
            <>
              <DropdownMenu.Item key="edit" onSelect={handleEdit}>
                <DropdownMenu.ItemIcon ios={{ name: 'pencil', color: colors.text_color_primary }} />
                <DropdownMenu.ItemTitle>{editLabel}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
              <DropdownMenu.Item key="copy" onSelect={handleCopyLink}>
                <DropdownMenu.ItemIcon ios={{ name: 'link', color: colors.text_color_primary }} />
                <DropdownMenu.ItemTitle>{copyLabel}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
              <DropdownMenu.Item key="delete" destructive onSelect={handleDelete}>
                <DropdownMenu.ItemIcon ios={{ name: 'trash' }} />
                <DropdownMenu.ItemTitle>{deleteLabel}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </View>
  );
}

AgentOwnerMenu.propTypes = {
  agent: PropTypes.object,
  size: PropTypes.number,
  iconSize: PropTypes.number,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onMenuStateChange: PropTypes.func,
};

export default React.memo(AgentOwnerMenu);
